import { AIS_VESSEL_CATEGORIES } from '../shared/models/ais-vessel-types';
import { PlaybackAvailableType } from '../shared/contracts/playback-controller';

/**
 * AISSTREAM'S PLAYBACK MATHS — the timeline, the step lookup and the position
 * interpolation that makes markers glide (D14).
 *
 * Extracted from `aisstream-playback.controller.ts` for one reason, and it is
 * the same reason phase 1 of this refactor was two pure extractions: this is
 * the only genuinely tricky code in that file, and until now no test could
 * reach it. Every function here was a private method on an `@Injectable` that
 * wants a Leaflet map and an SSE connection, so the interpolation's edge cases
 * — a hull absent from the next frame, two frames sharing a timestamp, a
 * virtual time past the end — were reachable only by opening the app.
 *
 * NOT extracted for symmetry with Voyage Radar. That file is 41 files because
 * it began as one 5,263-line controller; this one is under 800 and does not
 * have that problem. What it had was untestable logic, which is a different
 * complaint with a much smaller fix.
 *
 * Pure model file: no `@angular/*`, no `leaflet`, no `this` (rule R1). Vessels
 * stay `any` — their shape is the backend's `toVesselGeoJson()` envelope, this
 * provider's own dialect, and nothing here inspects more of it than the two
 * position fields it interpolates.
 */

/** One loaded history frame: an instant, and who was where at it. */
export interface AisstreamStep {
  time: string;
  vessels: any[];
}

/**
 * A history load, prepared for lookup: epoch times and per-frame MMSI maps,
 * index-aligned with the steps they came from.
 *
 * Precomputed once per load rather than derived per tick. The playback clock
 * runs at 5 ticks a second and every tick reads a frame, so parsing dates and
 * rebuilding maps in the render path is the difference between gliding and
 * stuttering.
 */
export interface AisstreamTimeline {
  /** Epoch ms per step. */
  times: number[];
  /** MMSI → vessel, per step. */
  maps: Array<Map<string, any>>;
}

/**
 * Key a vessel by MMSI, falling back to the feed's own row id.
 *
 * The fallback matters: interpolation pairs a hull in one frame with the SAME
 * hull in the next, and a vessel broadcasting no MMSI would otherwise key as
 * `'undefined'` — every anonymous hull in a frame collapsing onto one entry,
 * then gliding to whichever anonymous hull came last in the next.
 */
export function vesselKey(v: any): string {
  return String(v?.staticData?.mmsi ?? v?.id);
}

/** Build the per-load lookup tables. */
export function buildTimeline(steps: AisstreamStep[]): AisstreamTimeline {
  return {
    times: (steps ?? []).map((s) => new Date(s.time).getTime()),
    maps: (steps ?? []).map(
      (s) => new Map<string, any>((s.vessels ?? []).map((v) => [vesselKey(v), v])),
    ),
  };
}

/**
 * Index of the last step whose time is at or before `vt`.
 *
 * A forward scan rather than a binary search, deliberately: it is called once
 * per tick with a virtual time that has usually advanced by one tick, so it
 * almost always answers on the first or second comparison. A binary search
 * would be O(log n) every time where this is O(1) in the common case.
 *
 * Returns 0 for a virtual time before the first step — the first frame is the
 * earliest thing there is to draw, and returning -1 would blank the map at the
 * start of every replay.
 */
export function indexAtTime(times: number[], vt: number): number {
  let i = 0;
  while (i + 1 < times.length && times[i + 1] <= vt) i++;
  return i;
}

/**
 * Every vessel at an arbitrary point on the timeline, each one's position
 * linearly interpolated between the frames either side of it.
 *
 * This is what makes markers GLIDE instead of jumping frame to frame. The
 * frames arrive minutes apart; the clock ticks five times a second.
 *
 * Four rules, each of them a case that has to be decided rather than a
 * consequence of the formula:
 *
 *  - A hull with no position in the BASE frame is skipped entirely. There is
 *    nothing to draw and nothing to interpolate from.
 *  - A hull absent from the NEXT frame holds its base position. It has not
 *    stopped; we simply do not know where it went, and inventing motion for it
 *    would be drawing something the feed never said.
 *  - `frac` is clamped to [0, 1], so a virtual time outside the pair cannot
 *    extrapolate a vessel past the fix that was actually reported.
 *  - Two frames sharing a timestamp (`t1 <= t0`) give `frac = 0` rather than a
 *    division by zero — the base frame is drawn as-is.
 *
 * Returns COPIES with the interpolated position substituted; the loaded frames
 * are never mutated, so scrubbing backwards reads the same data it did going
 * forwards.
 */
export function interpolateAt(timeline: AisstreamTimeline, vt: number): any[] {
  const { times, maps } = timeline;
  if (!times.length) return [];
  const i = indexAtTime(times, vt);
  const baseMap = maps[i];
  if (!baseMap) return [];

  const nextMap = maps[i + 1];
  const t0 = times[i];
  const t1 = times[i + 1];
  const frac = nextMap && t1 > t0 ? Math.max(0, Math.min(1, (vt - t0) / (t1 - t0))) : 0;

  const vessels: any[] = [];
  baseMap.forEach((v, key) => {
    const p0 = v.lastPositionUpdate ?? {};
    if (p0.latitude == null || p0.longitude == null) return;

    let lat = p0.latitude;
    let lng = p0.longitude;
    const p1 = nextMap?.get(key)?.lastPositionUpdate;
    if (frac > 0 && p1?.latitude != null && p1?.longitude != null) {
      lat = p0.latitude + (p1.latitude - p0.latitude) * frac;
      lng = p0.longitude + (p1.longitude - p0.longitude) * frac;
    }
    vessels.push({ ...v, lastPositionUpdate: { ...p0, latitude: lat, longitude: lng } });
  });
  return vessels;
}

/**
 * The ship-type categories present in the given vessel lists, with counts —
 * what the filter chips offer.
 *
 * Takes a LIST OF LISTS so one function serves both modes: live passes the one
 * current frame, playback passes every loaded frame (a hull that has left the
 * view is still filterable in the frames that hold it).
 *
 * Ordered by `AIS_VESSEL_CATEGORIES` rather than by count, unlike Voyage
 * Radar's equivalent. That list is a fixed vocabulary with a fixed colour per
 * entry, so the chips keep their positions as traffic changes; Voyage Radar
 * derives its categories from whatever strings its GeoJSON happens to carry
 * and has no canonical order to hold to.
 */
export function availableTypes(vesselLists: any[][]): PlaybackAvailableType[] {
  const counts = new Map<string, number>();
  for (const list of vesselLists ?? []) {
    for (const v of list ?? []) {
      const code = String(v?.shipTypeCategory ?? 'OTHER');
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
  }
  return AIS_VESSEL_CATEGORIES
    .filter((c) => counts.has(c.code))
    .map((c) => ({ ...c, count: counts.get(c.code)! }));
}
